import { useMemo } from "react";
import {
  Box,
  Button,
  FormField,
  Input,
  Multiselect,
  SegmentedControl,
  Select,
  SpaceBetween,
} from "@cloudscape-design/components";
import type { MultiselectProps, SelectProps } from "@cloudscape-design/components";
import type { DetailWidget, RecordFilter } from "./viewContract";
import { type SchemaContentType } from "./schemaApi";

/**
 * Reusable, SELF-CONTAINED configuration form for ONE `detail` widget. It is a CONTROLLED
 * component: the parent owns the `DetailWidget` value and receives a replacement on every
 * edit via `onChange`. It mirrors TableWidgetConfig's data-binding UI (content type ->
 * relations -> fields) and then adds the detail-specific sections:
 *  - Columns: a Select of 1 / 2 / 3 (stored as a number),
 *  - Items: an ordered add/remove/key-value list (label Input + field Select over the
 *    selected fields, incl. relation paths + documentId),
 *  - Record filters: an add/remove list that PINS the view to a single record. Each row
 *    is a field Select (main scalars + documentId, NO relation paths) + a source control
 *    (Literal value | View parameter) + a conditional value Input or param Select.
 *
 * Cascading resets scoped to THIS widget value:
 *  - changing the content type clears relations/fields/items/recordFilters,
 *  - changing the selected fields drops items whose field is no longer selected and drops
 *    record filters whose field is no longer a valid main scalar (or documentId).
 */

/** Short name of a content type — leading segment of a stored relation field path. */
function shortNameFor(ct: SchemaContentType): string {
  return ct.apiName ?? ct.uid.split(".").pop() ?? ct.uid;
}

/**
 * Valid RECORD-FILTER field names: main-collection scalars PLUS "documentId", EXCLUDING
 * relation-path fields (`<mainShort>.*`). (No relation-path record filtering.)
 */
function validRecordFilterFieldSet(selectedFields: string[], mainShort: string): Set<string> {
  const prefix = `${mainShort}.`;
  const set = new Set(selectedFields.filter((f) => !f.startsWith(prefix)));
  set.add("documentId");
  return set;
}

export default function DetailWidgetConfig({
  value,
  schema,
  paramNames,
  onChange,
}: {
  value: DetailWidget;
  schema: SchemaContentType[];
  /** View-level param names (from PageEditorPage) offered in each param-sourced filter. */
  paramNames: string[];
  onChange: (next: DetailWidget) => void;
}) {
  const collection = value.collection;
  const selectedRelations = value.relations;
  const selectedFields = value.selected;
  const columns = value.columns;
  const items = value.items;
  const recordFilters = value.recordFilters;

  const currentCt = useMemo(
    () => schema.find((c) => c.uid === collection) ?? null,
    [schema, collection]
  );

  const collectionOptions: SelectProps.Option[] = useMemo(
    () => schema.map((c) => ({ label: c.displayName, value: c.uid })),
    [schema]
  );

  // To-one relations (one hop). Many-side and unknown relationTypes are excluded.
  const relationOptions: MultiselectProps.Option[] = useMemo(() => {
    if (!currentCt) return [];
    return currentCt.relations
      .filter((r) => r.relationType === "oneToOne" || r.relationType === "manyToOne")
      .map((r) => ({ label: r.name, value: r.name, description: `-> ${r.target ?? "?"}` }));
  }, [currentCt]);

  // UNION of the main type's own scalar fields and, per chosen relation, that relation
  // target's scalar fields emitted as dotted paths "<mainShort>.<relation>.<field>".
  const fieldOptions: MultiselectProps.Option[] = useMemo(() => {
    if (!currentCt) return [];
    const mainShort = shortNameFor(currentCt);
    const own: MultiselectProps.Option[] = currentCt.fields.map((f) => ({
      label: f.name,
      value: f.name,
    }));
    const related: MultiselectProps.Option[] = [];
    for (const relName of selectedRelations) {
      const rel = currentCt.relations.find((r) => r.name === relName);
      if (!rel || !rel.target) continue;
      const targetCt = schema.find((c) => c.uid === rel.target);
      if (!targetCt) continue;
      for (const f of targetCt.fields) {
        const path = `${mainShort}.${relName}.${f.name}`;
        related.push({ label: path, value: path, description: `via ${relName}` });
      }
    }
    return [...own, ...related];
  }, [currentCt, schema, selectedRelations]);

  // Item field options = the widget's selected fields (same set table columns use):
  // includes relation paths and documentId when selected.
  const selectedFieldOptions: SelectProps.Option[] = useMemo(
    () => selectedFields.map((f) => ({ label: f, value: f })),
    [selectedFields]
  );

  // Record-filter FIELD options: main-collection scalars (exclude relation paths) PLUS
  // "documentId". No relation-path record filtering.
  const recordFilterFieldOptions: SelectProps.Option[] = useMemo(() => {
    if (!currentCt) return [];
    const prefix = `${shortNameFor(currentCt)}.`;
    const scalars = selectedFields.filter((f) => f !== "documentId" && !f.startsWith(prefix));
    return [
      { label: "documentId", value: "documentId" },
      ...scalars.map((f) => ({ label: f, value: f })),
    ];
  }, [currentCt, selectedFields]);

  // Record-filter PARAM options: the view's defined param names (from PageEditorPage).
  const paramOptions: SelectProps.Option[] = useMemo(
    () => paramNames.map((p) => ({ label: p, value: p })),
    [paramNames]
  );

  const columnOptions: SelectProps.Option[] = [
    { label: "1 column", value: "1" },
    { label: "2 columns", value: "2" },
    { label: "3 columns", value: "3" },
  ];

  // --- Mutators: every edit produces a fresh DetailWidget via onChange ----------------

  const onCollectionChange = (uid: string) => {
    // Clear relations, fields, items and record filters.
    onChange({
      ...value,
      collection: uid,
      relations: [],
      selected: [],
      items: [],
      recordFilters: [],
    });
  };

  const onSelectedRelationsChange = (next: string[]) => {
    const removed = selectedRelations.filter((r) => !next.includes(r));
    if (removed.length === 0) {
      onChange({ ...value, relations: next });
      return;
    }
    const mainShort = currentCt ? shortNameFor(currentCt) : "";
    const belongsToRemoved = (field: string) =>
      removed.some((rel) => field.startsWith(`${mainShort}.${rel}.`));
    const nextSelected = selectedFields.filter((f) => !belongsToRemoved(f));
    onChange({
      ...value,
      relations: next,
      selected: nextSelected,
      // Drop items whose field is no longer selected.
      items: items.filter((it) => !it.field || nextSelected.includes(it.field)),
      // Relation removal never affects main scalars, so record filters are unaffected.
    });
  };

  const onSelectedFieldsChange = (next: string[]) => {
    const mainShort = currentCt ? shortNameFor(currentCt) : "";
    const validRf = validRecordFilterFieldSet(next, mainShort);
    onChange({
      ...value,
      selected: next,
      // Drop items whose field is no longer selected.
      items: items.filter((it) => !it.field || next.includes(it.field)),
      // Drop record filters whose field is no longer a valid main scalar (or documentId).
      recordFilters: recordFilters.filter((rf) => !rf.field || validRf.has(rf.field)),
    });
  };

  const onColumnsChange = (raw: string) => {
    const n = Number(raw);
    if (n === 1 || n === 2 || n === 3) onChange({ ...value, columns: n });
  };

  // --- Item row mutators (label + field) ---------------------------------------------
  const setItems = (updater: (prev: DetailWidget["items"]) => DetailWidget["items"]) =>
    onChange({ ...value, items: updater(items) });
  const addItem = () => setItems((prev) => [...prev, { label: "", field: "" }]);
  const removeItem = (index: number) => setItems((prev) => prev.filter((_, i) => i !== index));
  const setItemLabel = (index: number, label: string) =>
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, label } : it)));
  const setItemField = (index: number, field: string) =>
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, field } : it)));

  // --- Record-filter row mutators (field + source + value/param) ----------------------
  const setRecordFilters = (updater: (prev: RecordFilter[]) => RecordFilter[]) =>
    onChange({ ...value, recordFilters: updater(recordFilters) });
  const addRecordFilter = () =>
    setRecordFilters((prev) => [...prev, { field: "", source: "literal", value: "" }]);
  const removeRecordFilter = (index: number) =>
    setRecordFilters((prev) => prev.filter((_, i) => i !== index));
  const setRecordFilterField = (index: number, field: string) =>
    setRecordFilters((prev) => prev.map((rf, i) => (i === index ? { ...rf, field } : rf)));
  const setRecordFilterSource = (index: number, source: "literal" | "param") =>
    setRecordFilters((prev) =>
      prev.map((rf, i) =>
        i === index
          ? source === "literal"
            ? { field: rf.field, source, value: rf.value ?? "" }
            : { field: rf.field, source, param: rf.param ?? "" }
          : rf
      )
    );
  const setRecordFilterValue = (index: number, v: string) =>
    setRecordFilters((prev) => prev.map((rf, i) => (i === index ? { ...rf, value: v } : rf)));
  const setRecordFilterParam = (index: number, p: string) =>
    setRecordFilters((prev) => prev.map((rf, i) => (i === index ? { ...rf, param: p } : rf)));

  return (
    <SpaceBetween size="l">
      <Box variant="h4">Fields</Box>
      <FormField
        label="Content type"
        description="Select the single content type this detail view draws its record from."
      >
        <Select
          selectedOption={
            collection
              ? collectionOptions.find((o) => o.value === collection) ?? {
                  label: collection,
                  value: collection,
                }
              : null
          }
          options={collectionOptions}
          onChange={({ detail }) => onCollectionChange(detail.selectedOption.value ?? "")}
          placeholder="Choose a content type"
          empty="No content types available"
        />
      </FormField>

      {collection && (
        <FormField
          label="Relations"
          description="Optionally pick to-one relations (one hop) to expose their fields. Only oneToOne/manyToOne relations are available."
        >
          <Multiselect
            selectedOptions={selectedRelations.map(
              (r) => relationOptions.find((o) => o.value === r) ?? { label: r, value: r }
            )}
            options={relationOptions}
            onChange={({ detail }) =>
              onSelectedRelationsChange(
                detail.selectedOptions.map((o) => o.value).filter((v): v is string => Boolean(v))
              )
            }
            placeholder="Choose relations (optional)"
            empty="This content type has no to-one relations"
          />
        </FormField>
      )}

      {collection && (
        <FormField
          label="Fields"
          description="Choose which fields are available to the key-value items. Includes this content type's own fields plus the fields of any chosen relations (shown as dotted paths)."
        >
          <Multiselect
            selectedOptions={selectedFields.map(
              (f) => fieldOptions.find((o) => o.value === f) ?? { label: f, value: f }
            )}
            options={fieldOptions}
            onChange={({ detail }) =>
              onSelectedFieldsChange(
                detail.selectedOptions.map((o) => o.value).filter((v): v is string => Boolean(v))
              )
            }
            placeholder="Choose fields"
            empty="This content type has no selectable fields"
          />
        </FormField>
      )}

      <Box variant="h4">Layout</Box>
      <FormField
        label="Columns"
        description="How many columns the key-value grid is laid out in."
      >
        <Select
          selectedOption={columnOptions.find((o) => o.value === String(columns)) ?? columnOptions[1]}
          options={columnOptions}
          onChange={({ detail }) => onColumnsChange(detail.selectedOption.value ?? "2")}
        />
      </FormField>

      <FormField
        label="Items"
        description="Add, remove and order the key-value rows. Each row has a static label and maps to one selected field."
      >
        <SpaceBetween size="s">
          {items.length === 0 && <Box color="text-body-secondary">No items yet.</Box>}
          {items.map((item, index) => (
            <div key={`item-${index}`}>
              <SpaceBetween direction="horizontal" size="xs">
                <Input
                  value={item.label}
                  placeholder="Label"
                  onChange={({ detail }) => setItemLabel(index, detail.value)}
                />
                <Select
                  selectedOption={
                    item.field
                      ? selectedFieldOptions.find((o) => o.value === item.field) ?? {
                          label: item.field,
                          value: item.field,
                        }
                      : null
                  }
                  options={selectedFieldOptions}
                  onChange={({ detail }) => setItemField(index, detail.selectedOption.value ?? "")}
                  placeholder="Map to field"
                  empty="Select fields first"
                  disabled={selectedFields.length === 0}
                />
                <Button
                  iconName="close"
                  variant="icon"
                  ariaLabel={`Remove item ${index + 1}`}
                  onClick={() => removeItem(index)}
                />
              </SpaceBetween>
            </div>
          ))}
          <Button iconName="add-plus" onClick={addItem}>
            Add item
          </Button>
        </SpaceBetween>
      </FormField>

      <Box variant="h4">Record filters</Box>
      <FormField
        label="Record filters"
        description="Pin this view to a SINGLE record with exact-match filters (operator is always equals). Each filter's value is either a literal or a view parameter from the URL. A detail view should have at least one record filter; if the filters match zero or more than one record, the view shows an info message instead."
      >
        <SpaceBetween size="s">
          {!collection && <Box color="text-body-secondary">Select a content type first.</Box>}
          {collection && recordFilters.length === 0 && (
            <Box color="text-body-secondary">
              No record filters yet. Add at least one so the view resolves to a single record.
            </Box>
          )}
          {collection &&
            recordFilters.map((rf, index) => (
              <div key={`rf-${index}`}>
                <SpaceBetween direction="horizontal" size="xs">
                  <Select
                    selectedOption={
                      rf.field
                        ? recordFilterFieldOptions.find((o) => o.value === rf.field) ?? {
                            label: rf.field,
                            value: rf.field,
                          }
                        : null
                    }
                    options={recordFilterFieldOptions}
                    onChange={({ detail }) => setRecordFilterField(index, detail.selectedOption.value ?? "")}
                    placeholder="Field"
                    empty="Select fields first"
                  />
                  <SegmentedControl
                    selectedId={rf.source}
                    onChange={({ detail }) =>
                      setRecordFilterSource(index, detail.selectedId as "literal" | "param")
                    }
                    options={[
                      { id: "literal", text: "Literal value" },
                      { id: "param", text: "View parameter" },
                    ]}
                  />
                  {rf.source === "literal" ? (
                    <Input
                      value={rf.value ?? ""}
                      placeholder="Literal value"
                      onChange={({ detail }) => setRecordFilterValue(index, detail.value)}
                    />
                  ) : (
                    <Select
                      selectedOption={
                        rf.param
                          ? paramOptions.find((o) => o.value === rf.param) ?? {
                              label: rf.param,
                              value: rf.param,
                            }
                          : null
                      }
                      options={paramOptions}
                      onChange={({ detail }) => setRecordFilterParam(index, detail.selectedOption.value ?? "")}
                      placeholder="View parameter"
                      empty="No parameters defined"
                      disabled={paramNames.length === 0}
                    />
                  )}
                  <Button
                    iconName="close"
                    variant="icon"
                    ariaLabel={`Remove record filter ${index + 1}`}
                    onClick={() => removeRecordFilter(index)}
                  />
                </SpaceBetween>
              </div>
            ))}
          {collection && (
            <Button iconName="add-plus" onClick={addRecordFilter}>
              Add record filter
            </Button>
          )}
        </SpaceBetween>
      </FormField>
    </SpaceBetween>
  );
}

/** A fresh, empty detail widget used when the author adds a new detail card/tab. */
export function emptyDetailWidget(): DetailWidget {
  return { type: "detail", collection: "", relations: [], selected: [], columns: 2, items: [], recordFilters: [] };
}
